import { DatePipe, NgClass } from '@angular/common';
import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { IconField } from 'primeng/iconfield';
import { InputIcon } from 'primeng/inputicon';
import { InputText } from 'primeng/inputtext';
import { Paginator } from 'primeng/paginator';
import { Select } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { PageLayoutComponent } from '../../shared/page-layout/page-layout';
import { AuditHistoryService, AuditHistory } from '../../../service/audit-history.service';
import { StateContainerComponent, ViewState } from '../../shared/state-container/state-container.component';

@Component({
  selector: 'app-audit-logs',
  imports: [
    DatePipe,
    FormsModule,
    IconField,
    InputIcon,
    InputText,
    PageLayoutComponent,
    Paginator,
    Select,
    TableModule,
    NgClass,
    StateContainerComponent
  ],
  templateUrl: './audit-logs.html',
  styleUrl: './audit-logs.css',
})
export class AuditLogs implements OnInit {
  private auditHistoryService = inject(AuditHistoryService);

  readonly logs = signal<AuditHistory[]>([]);
  readonly totalRecords = signal(0);
  readonly loading = signal(false);
  readonly error = signal<any>(null);

  searchTerm = '';
  groupFilter: 'Dealer' | 'Vendor' | null = null;
  resultFilter: 'SUCCESS' | 'FAILED' | null = null;

  rows = 10;
  first = 0;

  readonly groupOptions = [
    { label: 'All Groups', value: null },
    { label: 'Dealers', value: 'Dealer' },
    { label: 'Vendors', value: 'Vendor' },
  ];

  readonly resultOptions = [
    { label: 'All Results', value: null },
    { label: 'Success', value: 'SUCCESS' },
    { label: 'Failed', value: 'FAILED' },
  ];

  readonly visibleLogs = computed(() => {
    const search = this.searchTerm.trim().toLowerCase();
    return this.logs().filter(log => {
      const matchesGroup = !this.groupFilter || this.getGroupFromUser(log) === this.groupFilter;
      const matchesSearch = !search || [log.event, log.actor, log.userId]
        .some(value => value?.toLowerCase().includes(search));

      return matchesGroup && matchesSearch;
    });
  });

  // Computed state for StateContainer
  readonly viewState = computed<ViewState>(() => {
    if (this.loading()) return 'loading';
    if (this.error()) return 'error';
    if (this.visibleLogs().length === 0) return 'empty';
    return 'content';
  });

  readonly showClearFilters = computed(() => {
    return !!(this.searchTerm || this.groupFilter || this.resultFilter);
  });

  readonly emptyStateMessage = computed(() => {
    if (this.searchTerm || this.groupFilter || this.resultFilter) {
      return 'No audit logs match your current search or filter criteria. Try adjusting your filters.';
    }
    return 'No audit logs found. Events will appear here as they occur.';
  });

  readonly errorMessage = computed(() => {
    const err = this.error();
    if (typeof err === 'string') return err;
    if (err?.message) return err.message;
    return 'Failed to load audit logs. Please try again.';
  });

  ngOnInit() {
    this.loadAuditLogs();
  }

  loadAuditLogs() {
    this.loading.set(true);
    this.error.set(null);

    const requestBody: any = {
      query: this.searchTerm || undefined,
      result: this.resultFilter || undefined,
    };

    this.auditHistoryService.searchAuditHistory(
      this.first / this.rows,
      this.rows,
      requestBody
    ).subscribe({
      next: (res) => {
        this.logs.set(res.content || []);
        this.totalRecords.set(res.page?.totalElements || 0);
        this.loading.set(false);
      },
      error: (err) => {
        this.error.set(err);
        this.loading.set(false);
      }
    });
  }

  // Retry method for error state
  retryLoad() {
    this.loadAuditLogs();
  }

  clearFilters() {
    this.searchTerm = '';
    this.groupFilter = null;
    this.resultFilter = null;
    this.first = 0;
    this.loadAuditLogs();
  }

  getGroupFromUser(log: AuditHistory): string {
    if (log.userId?.toLowerCase().includes('dealer')) return 'Dealer';
    if (log.userId?.toLowerCase().includes('vendor')) return 'Vendor';
    if (log.actor?.toLowerCase().includes('dealer')) return 'Dealer';
    if (log.actor?.toLowerCase().includes('vendor')) return 'Vendor';
    return 'Dealer'; 
  }

  onSearch(): void {
    this.first = 0;
    this.loadAuditLogs();
  }

  setGroupFilter(value: 'Dealer' | 'Vendor' | null): void {
    this.groupFilter = value;
    this.first = 0;
  }

  setResultFilter(value: 'SUCCESS' | 'FAILED' | null): void {
    this.resultFilter = value;
    this.first = 0;
    this.loadAuditLogs();
  }

  onPageChange(event: { first?: number; rows?: number }): void {
    this.first = event.first ?? 0;
    this.rows = event.rows ?? this.rows;
    this.loadAuditLogs();
  }
}